<template>
  <div class="sparkline-container">
    <svg
      :width="width"
      :height="height"
      class="sparkline"
      :viewBox="`0 0 ${width} ${height}`">
      <defs>
        <linearGradient
          :id="gradientId"
          x1="0%"
          y1="0%"
          x2="0%"
          y2="100%">
          <stop
            offset="0%"
            :style="`stop-color:${color};stop-opacity:0.4`" />
          <stop
            offset="100%"
            :style="`stop-color:${color};stop-opacity:0.1`" />
        </linearGradient>
      </defs>
      <path
        v-if="fillPath"
        :d="fillPath"
        :fill="`url(#${gradientId})`"
        stroke="none" />
      <path
        v-if="linePath"
        :d="linePath"
        :stroke="color"
        :stroke-width="strokeWidth"
        fill="none"
        stroke-linecap="round"
        stroke-linejoin="round" />
      <circle
        v-if="showDot && lastPoint"
        :cx="lastPoint.x"
        :cy="lastPoint.y"
        :r="3"
        :fill="color"
        class="sparkline-dot" />
    </svg>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  data: {
    type: Array,
    required: true
  },
  width: {
    type: Number,
    default: 100
  },
  height: {
    type: Number,
    default: 30
  },
  color: {
    type: String,
    default: '#3b82f6'
  },
  strokeWidth: {
    type: Number,
    default: 2
  },
  showDot: {
    type: Boolean,
    default: true
  }
})

const gradientId = computed(() => `gradient-${Math.random().toString(36).substr(2, 9)}`)

const normalizedData = computed(() => {
  if (!props.data || props.data.length === 0) return []

  const min = Math.min(...props.data)
  const max = Math.max(...props.data)
  const range = max - min || 1

  return props.data.map((value, index) => ({
    x: (index / (props.data.length - 1)) * props.width,
    y: props.height - ((value - min) / range) * (props.height - props.strokeWidth * 2) - props.strokeWidth
  }))
})

const linePath = computed(() => {
  if (normalizedData.value.length < 2) return ''

  return normalizedData.value.reduce(
    (path, point, index) => path + (index === 0 ? `M ${point.x},${point.y}` : ` L ${point.x},${point.y}`),
    ''
  )
})

const fillPath = computed(() => {
  if (normalizedData.value.length < 2) return ''

  const line = linePath.value
  const firstPoint = normalizedData.value[0]
  const lastPoint = normalizedData.value[normalizedData.value.length - 1]

  return `${line} L ${lastPoint.x},${props.height} L ${firstPoint.x},${props.height} Z`
})

const lastPoint = computed(() => normalizedData.value[normalizedData.value.length - 1])
</script>

<style scoped>
.sparkline-container {
  display: inline-block;
}

.sparkline-dot {
  filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.2));
}
</style>
